import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthProvider';
import DecisionWidget from './DecisionWidget';

const API_URL = 'https://mizar-api.vercel.app';
const SIGNAL_ENDPOINT = `${API_URL}/v1/signal`;

const ORIGINS = [
  { code: 'MAN', label: 'Manchester (MAN)' },
  { code: 'LHR', label: 'London Heathrow (LHR)' },
  { code: 'LGW', label: 'London Gatwick (LGW)' },
  { code: 'EDI', label: 'Edinburgh (EDI)' },
  { code: 'BRS', label: 'Bristol (BRS)' },
  { code: 'BHX', label: 'Birmingham (BHX)' },
  { code: 'LPL', label: 'Liverpool (LPL)' },
  { code: 'NCL', label: 'Newcastle (NCL)' },
  { code: 'GLA', label: 'Glasgow (GLA)' }
];

const DESTINATIONS = [
  { code: 'BCN', label: 'Barcelona' },
  { code: 'CDG', label: 'Paris' },
  { code: 'AMS', label: 'Amsterdam' },
  { code: 'FCO', label: 'Rome' },
  { code: 'MAD', label: 'Madrid' },
  { code: 'LIS', label: 'Lisbon' },
  { code: 'ATH', label: 'Athens' },
  { code: 'PRG', label: 'Prague' },
  { code: 'VIE', label: 'Vienna' },
  { code: 'ZRH', label: 'Zurich' },
  { code: 'CPH', label: 'Copenhagen' },
  { code: 'JFK', label: 'New York' },
  { code: 'LAX', label: 'Los Angeles' },
  { code: 'ORD', label: 'Chicago' },
  { code: 'YYZ', label: 'Toronto' },
  { code: 'SIN', label: 'Singapore' },
  { code: 'BKK', label: 'Bangkok' },
  { code: 'HKG', label: 'Hong Kong' },
  { code: 'NRT', label: 'Tokyo' },
  { code: 'JNB', label: 'Johannesburg' },
  { code: 'CPT', label: 'Cape Town' }
];

const CABIN_CLASSES = [
  { value: 'economy', label: 'Economy' },
  { value: 'premium_economy', label: 'Premium Economy' },
  { value: 'business', label: 'Business' },
  { value: 'first', label: 'First' }
];

function offsetDate(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function getVariant() {
  const key = 'mizar_demo_variant';
  const existing = window.localStorage.getItem(key);
  if (existing === 'control' || existing === 'treatment') return existing;

  const next = Math.random() < 0.5 ? 'control' : 'treatment';
  window.localStorage.setItem(key, next);
  return next;
}

export default function DemoWidget() {
  const { supabase, user } = useAuth();

  const [tripType, setTripType] = useState('return');
  const [origin, setOrigin] = useState('MAN');
  const [destination, setDestination] = useState('BCN');
  const [outboundDate, setOutboundDate] = useState(offsetDate(30));
  const [returnDate, setReturnDate] = useState(offsetDate(37));
  const [cabinClass, setCabinClass] = useState('economy');
  const [price, setPrice] = useState('285');
  const [apiKey, setApiKey] = useState('');
  const [keyLoading, setKeyLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [requestMeta, setRequestMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [variant, setVariant] = useState('treatment');

  useEffect(() => {
    setVariant(getVariant());
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setKeyLoading(false);
      return;
    }

    supabase
      .from('api_keys_v2')
      .select('api_key')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .single()
      .then(({ data }) => {
        if (data?.api_key) {
          setApiKey(data.api_key);
        }
        setKeyLoading(false);
      })
      .catch(() => setKeyLoading(false));
  }, [user?.id, supabase]);

  const checkFare = async () => {
    if (!apiKey || loading) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        origin,
        destination,
        outbound_date: outboundDate,
        price_gbp: parseFloat(price) || 285,
        session_id: `demo_widget_${variant}`,
        client_platform: 'web',
        cabin_class: cabinClass,
        trip_type: tripType
      };

      if (tripType === 'return') {
        payload.return_date = returnDate;
      }

      const res = await fetch(SIGNAL_ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const text = await res.text();

      if (!res.ok) {
        throw new Error(`${res.status}: ${text} | URL: ${SIGNAL_ENDPOINT}`);
      }

      const data = JSON.parse(text);

      setRequestMeta({
        origin,
        destination,
        tripType,
        cabinClass,
        outboundDate,
        returnDate: tripType === 'return' ? returnDate : null,
        price: parseFloat(price) || 285,
        variant
      });

      setResult(data);
    } catch (err) {
      setError(err.message || `Request failed | URL: ${SIGNAL_ENDPOINT}`);
    } finally {
      setLoading(false);
    }
  };

  if (keyLoading) {
    return (
      <div style={{ color: 'var(--text-muted)', fontSize: '14px', padding: '40px 0' }}>
        Loading...
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '10px',
          padding: '32px',
          textAlign: 'center',
          maxWidth: '480px'
        }}
      >
        <div
          style={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            fontSize: '1rem',
            marginBottom: '8px'
          }}
        >
          No active API key found
        </div>
        <div
          style={{
            color: 'var(--text-secondary)',
            fontSize: '14px'
          }}
        >
          Generate an API key in the API Keys tab to use the demo.
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .demo-wrap {
          max-width: 720px;
        }

        .demo-intro {
          margin-bottom: 24px;
          color: var(--text-secondary);
          font-size: 0.92rem;
          line-height: 1.7;
        }

        .demo-form {
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: 12px;
          padding: 28px;
          margin-bottom: 24px;
        }

        .demo-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }

        .demo-row.dates-row {
          grid-template-columns: 1fr 1fr;
        }

        .demo-row.dates-row.oneway {
          grid-template-columns: 1fr;
        }

        .demo-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .demo-label {
          font-family: 'Manrope', sans-serif;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--text-secondary);
        }

        .demo-select,
        .demo-input {
          padding: 10px 12px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          color: var(--text-primary);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          width: 100%;
          transition: border-color 0.15s ease;
        }

        .demo-select:focus,
        .demo-input:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .demo-price-wrap {
          position: relative;
        }

        .demo-price-prefix {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-secondary);
          font-size: 0.95rem;
          pointer-events: none;
        }

        .demo-price-input {
          padding-left: 24px;
        }

        .trip-toggle {
          display: inline-flex;
          gap: 8px;
          margin-bottom: 18px;
          flex-wrap: wrap;
        }

        .trip-toggle-btn {
          padding: 9px 14px;
          border-radius: 999px;
          border: 1px solid var(--border-primary);
          background: transparent;
          color: var(--text-secondary);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.88rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .trip-toggle-btn:hover {
          border-color: var(--accent-primary);
          color: var(--text-primary);
        }

        .trip-toggle-btn.active {
          background: rgba(217, 118, 66, 0.08);
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .demo-submit {
          width: 100%;
          padding: 14px;
          background: var(--text-primary);
          border: none;
          border-radius: 8px;
          color: var(--bg-primary);
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
          letter-spacing: 0.02em;
          cursor: pointer;
          transition: opacity 0.15s ease;
          margin-top: 8px;
        }

        .demo-submit:hover:not(:disabled) {
          opacity: 0.85;
        }

        .demo-submit:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .demo-error {
          margin-top: 16px;
          padding: 12px 14px;
          background: rgba(192, 57, 43, 0.08);
          border: 1px solid rgba(192, 57, 43, 0.3);
          border-radius: 8px;
          color: var(--accent-red);
          font-size: 0.85rem;
          line-height: 1.5;
          word-break: break-word;
        }

        .demo-note {
          margin-top: 12px;
          color: var(--text-muted);
          font-size: 0.78rem;
          line-height: 1.5;
        }

        @media (max-width: 640px) {
          .demo-form {
            padding: 20px;
          }

          .demo-row {
            grid-template-columns: 1fr;
            gap: 12px;
            margin-bottom: 12px;
          }

          .demo-row.dates-row {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          .demo-row.dates-row.oneway {
            grid-template-columns: 1fr;
          }

          .demo-select,
          .demo-input {
            min-height: 44px;
          }

          .trip-toggle {
            display: grid;
            grid-template-columns: 1fr 1fr;
            width: 100%;
          }

          .trip-toggle-btn {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>

      <div className="demo-wrap">
        <p className="demo-intro">
          Enter a live route and fare to see how RegretRisk behaves in the booking moment. This demo
          uses the production API and returns the live signal in real time.
        </p>

        <div className="demo-form">
          <div className="trip-toggle">
            <button
              type="button"
              className={`trip-toggle-btn ${tripType === 'return' ? 'active' : ''}`}
              onClick={() => setTripType('return')}
            >
              Return
            </button>
            <button
              type="button"
              className={`trip-toggle-btn ${tripType === 'oneway' ? 'active' : ''}`}
              onClick={() => setTripType('oneway')}
            >
              One-way
            </button>
          </div>

          <div className="demo-row">
            <div className="demo-field">
              <label className="demo-label">Flying from</label>
              <select
                className="demo-select"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
              >
                {ORIGINS.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="demo-field">
              <label className="demo-label">Flying to</label>
              <select
                className="demo-select"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
              >
                {DESTINATIONS.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="demo-row">
            <div className="demo-field">
              <label className="demo-label">Cabin class</label>
              <select
                className="demo-select"
                value={cabinClass}
                onChange={(e) => setCabinClass(e.target.value)}
              >
                {CABIN_CLASSES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="demo-field">
              <label className="demo-label">Current fare</label>
              <div className="demo-price-wrap">
                <span className="demo-price-prefix">£</span>
                <input
                  type="number"
                  className="demo-input demo-price-input"
                  value={price}
                  min="1"
                  max="99999"
                  step="1"
                  placeholder="285"
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className={`demo-row dates-row ${tripType === 'oneway' ? 'oneway' : ''}`}>
            <div className="demo-field">
              <label className="demo-label">Outbound date</label>
              <input
                type="date"
                className="demo-input"
                value={outboundDate}
                min={offsetDate(1)}
                onChange={(e) => setOutboundDate(e.target.value)}
              />
            </div>

            {tripType === 'return' && (
              <div className="demo-field">
                <label className="demo-label">Return date</label>
                <input
                  type="date"
                  className="demo-input"
                  value={returnDate}
                  min={outboundDate}
                  onChange={(e) => setReturnDate(e.target.value)}
                />
              </div>
            )}
          </div>

          <button
            className="demo-submit"
            onClick={checkFare}
            disabled={
              loading ||
              !origin ||
              !destination ||
              !outboundDate ||
              !price ||
              (tripType === 'return' && !returnDate)
            }
          >
            {loading ? 'Checking fare...' : 'Check this fare →'}
          </button>

          <div className="demo-note">
            Demo variant: <strong>{variant}</strong>
          </div>

          {error && <div className="demo-error">Could not retrieve signal. {error}</div>}
        </div>

        {result && (
          <DecisionWidget
            prediction={result}
            meta={requestMeta}
            variant={variant}
          />
        )}
      </div>
    </>
  );
}